<template>
  <div class="configuracion-facturacion py-4" :class="themeClass">
    <div class="container-fluid">
      <div class="row justify-content-center">
        <div class="col-xxl-9 col-xl-10">
          <div class="card shadow-sm border-0">
            <div class="card-header bg-gradient text-white d-flex justify-content-between align-items-center">
              <div>
                <h3 class="mb-0">Configuración de Facturación</h3>
                <small class="text-white-50">Esta configuración aplica para la empresa vinculada al usuario actual.</small>
              </div>
              <span class="badge bg-light text-primary fw-semibold">Empresa activa</span>
            </div>
            <div class="card-body">
              <div v-if="loading" class="text-center py-5">
                <div class="spinner-border text-primary"></div>
                <p class="text-muted mt-3">Cargando configuración...</p>
              </div>
              <template v-else>
                <div v-if="errorMessage" class="alert alert-danger d-flex justify-content-between align-items-center" role="alert">
                  <span>{{ errorMessage }}</span>
                  <button type="button" class="btn-close" aria-label="Cerrar" @click="errorMessage = ''"></button>
                </div>
                <div v-if="successMessage" class="alert alert-success d-flex justify-content-between align-items-center" role="alert">
                  <span>{{ successMessage }}</span>
                  <button type="button" class="btn-close" aria-label="Cerrar" @click="successMessage = ''"></button>
                </div>

                <!-- Identificación del contribuyente -->
                <section class="section-card">
                  <header class="section-card__header">
                    <div>
                      <h5 class="mb-0">Identificación del contribuyente</h5>
                      <small class="text-muted">Datos informativos obtenidos de la empresa registrada</small>
                    </div>
                  </header>
                  <div class="section-card__body">
                    <div class="row g-3">
                      <div class="col-md-4">
                        <label class="form-label">RUC</label>
                        <input
                          v-model="form.ruc"
                          type="text"
                          class="form-control"
                          readonly
                        />
                      </div>
                      <div class="col-md-8">
                        <label class="form-label">Razón social</label>
                        <input
                          v-model="form.razonSocial"
                          type="text"
                          class="form-control"
                          readonly
                        />
                      </div>
                    </div>
                    <div class="row g-3 mt-1">
                      <div class="col-md-6">
                        <label class="form-label">Nombre comercial</label>
                        <input
                          v-model.trim="form.nombreComercial"
                          type="text"
                          class="form-control"
                          placeholder="Ej. Comercial Factured"
                        />
                      </div>
                      <div class="col-md-3">
                        <label class="form-label">Ciudad</label>
                        <input
                          v-model.trim="form.ciudad"
                          type="text"
                          class="form-control"
                          placeholder="Ej. Quito"
                        />
                      </div>
                      <div class="col-md-3">
                        <label class="form-label">Teléfonos</label>
                        <input
                          v-model.trim="form.telefonos"
                          type="text"
                          class="form-control"
                          placeholder="Separar con coma si son varios"
                        />
                      </div>
                    </div>
                    <div class="row g-3 mt-1">
                      <div class="col-12">
                        <label class="form-label">Dirección matriz / principal</label>
                        <input
                          v-model.trim="form.direccion"
                          type="text"
                          class="form-control"
                          placeholder="Dirección completa"
                        />
                      </div>
                    </div>
                  </div>
                </section>

                <!-- Condición tributaria -->
                <section class="section-card">
                  <header class="section-card__header">
                    <div>
                      <h5 class="mb-0">Condición tributaria</h5>
                      <small class="text-muted">Define obligaciones y categorías especiales de la empresa</small>
                    </div>
                  </header>
                  <div class="section-card__body">
                    <div class="row g-3 align-items-center">
                      <div class="col-md-4">
                        <label class="form-label">Obligado a llevar contabilidad</label>
                        <div class="form-check form-switch">
                          <input
                            v-model="form.obligadoContabilidad"
                            class="form-check-input"
                            type="checkbox"
                            role="switch"
                            id="obligadoContabilidadSwitch"
                          />
                          <label class="form-check-label" for="obligadoContabilidadSwitch">
                            {{ form.obligadoContabilidad ? 'Sí, obligado' : 'No obligado' }}
                          </label>
                        </div>
                      </div>
                      <div class="col-md-4">
                        <label class="form-label">Contribuyente especial</label>
                        <div class="form-check">
                          <input
                            v-model="form.contribuyenteEspecial.activo"
                            class="form-check-input"
                            type="checkbox"
                            id="contribuyenteEspecialCheck"
                          />
                          <label class="form-check-label" for="contribuyenteEspecialCheck">Marcar si aplica</label>
                        </div>
                        <div v-if="form.contribuyenteEspecial.activo" class="mt-2">
                          <input
                            v-model.trim="form.contribuyenteEspecial.codigo"
                            type="text"
                            class="form-control"
                            placeholder="Código de resolución"
                          />
                        </div>
                      </div>
                      <div class="col-md-4">
                        <label class="form-label">Gran contribuyente</label>
                        <div class="form-check">
                          <input
                            v-model="form.granContribuyente.activo"
                            class="form-check-input"
                            type="checkbox"
                            id="granContribuyenteCheck"
                          />
                          <label class="form-check-label" for="granContribuyenteCheck">Marcar si aplica</label>
                        </div>
                        <div v-if="form.granContribuyente.activo" class="mt-2">
                          <input
                            v-model.trim="form.granContribuyente.codigo"
                            type="text"
                            class="form-control"
                            placeholder="Código de resolución"
                          />
                        </div>
                      </div>
                    </div>

                    <div class="row g-3 mt-1">
                      <div class="col-md-4">
                        <div class="form-check mb-2">
                          <input
                            v-model="form.regimenGeneral"
                            class="form-check-input"
                            type="checkbox"
                            id="regimenGeneralCheck"
                          />
                          <label class="form-check-label" for="regimenGeneralCheck">
                            Contribuyente Régimen General
                          </label>
                        </div>
                      </div>
                      <div class="col-md-4">
                        <div class="form-check mb-2">
                          <input
                            v-model="form.regimenRimpe.activo"
                            class="form-check-input"
                            type="checkbox"
                            id="rimpeCheck"
                          />
                          <label class="form-check-label" for="rimpeCheck">
                            Contribuyente régimen RIMPE / Negocio popular
                          </label>
                        </div>
                        <div class="ms-3" v-if="form.regimenRimpe.activo">
                          <div
                            v-for="tipo in rimpeTipos"
                            :key="tipo.value"
                            class="form-check"
                          >
                            <input
                              class="form-check-input"
                              type="radio"
                              :id="`rimpe-${tipo.value}`"
                              :value="tipo.value"
                              v-model="form.regimenRimpe.tipo"
                            />
                            <label class="form-check-label" :for="`rimpe-${tipo.value}`">{{ tipo.label }}</label>
                          </div>
                        </div>
                      </div>
                      <div class="col-md-4">
                        <div class="form-check mb-2">
                          <input
                            v-model="form.agenteRetencion.activo"
                            class="form-check-input"
                            type="checkbox"
                            id="agenteRetencionCheck"
                          />
                          <label class="form-check-label" for="agenteRetencionCheck">
                            Agente de retención
                          </label>
                        </div>
                        <div class="ms-3" v-if="form.agenteRetencion.activo">
                          <select v-model="form.agenteRetencion.tipo" class="form-select">
                            <option value="">Seleccione el tipo de agente</option>
                            <option v-for="opcion in availableAgenteTipos" :key="opcion.value || opcion" :value="opcion.value || opcion">
                              {{ opcion.label || opcion }}
                            </option>
                          </select>
                          <small class="text-muted">El catálogo se integra desde base de datos. Valores dummy mientras se implementa.</small>
                        </div>
                      </div>
                    </div>
                  </div>
                </section>

                <!-- Tipo de ambiente SRI -->
                <section class="section-card">
                  <header class="section-card__header">
                    <div>
                      <h5 class="mb-0">Ambiente de facturación (SRI)</h5>
                      <small class="text-muted">Define si los comprobantes se envían al ambiente de pruebas o de producción del SRI</small>
                    </div>
                  </header>
                  <div class="section-card__body">
                    <div class="row g-3 align-items-start">
                      <div class="col-md-4">
                        <label class="form-label">Tipo de ambiente</label>
                        <select class="form-select" v-model="form.ambiente">
                          <option value="1">PRUEBAS</option>
                          <option value="2">PRODUCCIÓN</option>
                        </select>
                        <small class="text-muted">
                          Use <strong>PRUEBAS</strong> mientras realiza validaciones con el SRI.
                          Cambie a <strong>PRODUCCIÓN</strong> únicamente cuando esté autorizado.
                        </small>
                      </div>
                      <div class="col-md-8">
                        <div class="alert mb-0" :class="form.ambiente === '2' || form.ambiente === 2 ? 'alert-success' : 'alert-warning'">
                          <i :class="form.ambiente === '2' || form.ambiente === 2 ? 'bi bi-check-circle-fill' : 'bi bi-exclamation-triangle-fill'" class="me-2"></i>
                          <strong v-if="form.ambiente === '2' || form.ambiente === 2">Producción:</strong>
                          <strong v-else>Pruebas:</strong>
                          <span v-if="form.ambiente === '2' || form.ambiente === 2"> Los comprobantes generados son válidos y tienen validez tributaria ante el SRI.</span>
                          <span v-else> Los comprobantes se envían al ambiente de pruebas. No tienen validez tributaria.</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </section>

                <!-- Establecimientos -->
                <!-- ...sección de establecimientos eliminada, ahora gestionada en Secuenciales... -->

                <!-- Certificado digital -->
                <section class="section-card">
                  <header class="section-card__header">
                    <div>
                      <h5 class="mb-0">Certificado digital y firma</h5>
                      <small class="text-muted">Carga el archivo .p12 y define cómo se firmarán los documentos</small>
                    </div>
                  </header>
                  <div class="section-card__body">
                    <div class="row g-3">
                      <div class="col-lg-6">
                        <label class="form-label">Archivo .p12</label>
                        <input type="file" class="form-control" accept=".p12" @change="onCertFileChange" />
                        <small class="text-muted">Solo se aceptan certificados en formato .p12</small>
                        <!-- Certificado activo cargado desde la base de datos -->
                        <div
                          v-if="digitalCertificate.cargadoDesdeDB && !digitalCertificate.archivo"
                          :class="certExpiryClass + ' mt-2 py-2 px-3'"
                        >
                          <i class="bi bi-shield-check me-1"></i>
                          <strong>Certificado activo:</strong> {{ digitalCertificate.nombreArchivo }}
                          <span v-if="digitalCertificate.fechaCaducidad" class="ms-2">
                            &middot; Vence: {{ digitalCertificate.fechaCaducidad }}
                            <span v-if="certCaducado" class="badge bg-danger ms-1">EXPIRADO</span>
                            <span v-else-if="certProximoAVencer" class="badge bg-warning text-dark ms-1">Próximo a vencer</span>
                          </span>
                        </div>
                        <!-- Nuevo archivo seleccionado localmente -->
                        <div
                          v-else-if="digitalCertificate.archivo"
                          class="alert alert-secondary mt-2 py-2 px-3"
                        >
                          <strong>Archivo seleccionado:</strong> {{ digitalCertificate.nombreArchivo }}
                        </div>
                      </div>
                      <div class="col-lg-6">
                        <label class="form-label">Clave de la firma</label>
                        <input
                          v-model="digitalCertificate.clave"
                          type="password"
                          class="form-control"
                          placeholder="Clave del certificado"
                        />
                        <small class="text-muted">
                          La clave se cifrará antes de enviarse. Nunca se expone en texto plano.
                        </small>
                      </div>
                    </div>

                    <div class="row g-3 mt-1 align-items-end">
                      <div class="col-md-4">
                        <label class="form-label">Fecha de caducidad</label>
                        <input
                          v-model="digitalCertificate.fechaCaducidad"
                          type="text"
                          class="form-control"
                          placeholder="Se detectará automáticamente"
                          readonly
                        />
                      </div>
                      <div class="col-md-4">
                        <label class="form-label">Firma automática</label>
                        <div class="form-check form-switch">
                          <input
                            v-model="digitalCertificate.firmarAutomaticamente"
                            class="form-check-input"
                            type="checkbox"
                            id="firmaAutoSwitch"
                          />
                          <label class="form-check-label" for="firmaAutoSwitch">
                            {{ digitalCertificate.firmarAutomaticamente ? 'Firmar documentos automáticamente' : 'Solicitar clave por documento' }}
                          </label>
                        </div>
                      </div>
                      <div class="col-md-4 d-grid">
                        <button
                          class="btn btn-outline-secondary"
                          type="button"
                          @click="validarCertificado"
                          :disabled="!digitalCertificate.archivo || !digitalCertificate.clave || validatingCert"
                        >
                          <span v-if="validatingCert" class="spinner-border spinner-border-sm me-2"></span>
                          Validar certificado
                        </button>
                      </div>
                    </div>

                    <div class="mt-3">
                      <button
                        class="btn btn-outline-info"
                        type="button"
                        @click="solicitarAmbienteProduccion"
                        :disabled="requestingAmbiente"
                      >
                        <span v-if="requestingAmbiente" class="spinner-border spinner-border-sm me-2"></span>
                        Solicitar ambiente de producción al SRI
                      </button>
                    </div>
                  </div>
                </section>

                <!-- Identidad visual -->
                <section class="section-card">
                  <header class="section-card__header">
                    <div>
                      <h5 class="mb-0">Identidad visual en facturas</h5>
                      <small class="text-muted">Personaliza el logotipo y mensajes que verán tus clientes</small>
                    </div>
                  </header>
                  <div class="section-card__body">
                    <div class="row g-3">
                      <div class="col-md-6">
                        <label class="form-label">Logo de la empresa</label>
                        <input type="file" class="form-control" accept="image/*" @change="onLogoFileChange" />
                        <small class="text-muted">Formatos recomendados: PNG o JPG (max 1MB)</small>
                        <div v-if="logo.preview" class="logo-preview mt-3 p-3 border rounded">
                          <img :src="logo.preview" alt="Logo preview" class="img-fluid" />
                        </div>
                      </div>
                      <div class="col-md-6">
                        <label class="form-label">Mensaje personalizado en facturas</label>
                        <textarea
                          v-model.trim="form.mensajeFactura"
                          rows="6"
                          class="form-control"
                          placeholder="Ej. ¡Gracias por su compra! Garantía de 30 días en todos nuestros productos."
                        ></textarea>
                      </div>
                    </div>
                  </div>
                </section>

                <div class="d-flex justify-content-end gap-2">
                  <button class="btn btn-outline-secondary" type="button" @click="cargarDatos" :disabled="saving">
                    <i class="bi bi-arrow-clockwise me-1"></i> Reestablecer datos
                  </button>
                  <button class="btn btn-primary" type="button" @click="guardarConfiguracion" :disabled="saving">
                    <span v-if="saving" class="spinner-border spinner-border-sm me-2"></span>
                    Guardar configuración
                  </button>
                </div>
              </template>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import themeManager from '@/store/theme'
import ConfiguracionService from '@/services/configuracion'
import { obtenerPerfil as fetchCurrentUser } from '@/services/servicioAutenticacion'

export default {
  name: 'ClientConfiguracionFacturacion',
  data() {
    return {
      loading: true,
      saving: false,
      requestingAmbiente: false,
      validatingCert: false,
      errorMessage: '',
      successMessage: '',
      form: {
        ruc: '',
        razonSocial: '',
        nombreComercial: '',
        ciudad: '',
        telefonos: '',
        direccion: '',
        puntoEmisionPrincipal: '',
        ambiente: '1',
        obligadoContabilidad: false,
        contribuyenteEspecial: { activo: false, codigo: '' },
        granContribuyente: { activo: false, codigo: '' },
        regimenGeneral: false,
        regimenRimpe: { activo: false, tipo: 'rimpe' },
        agenteRetencion: { activo: false, tipo: '' },
        mensajeFactura: ''
      },
      digitalCertificate: {
        archivo: null,
        nombreArchivo: '',
        fechaCaducidad: '',
        clave: '',
        firmarAutomaticamente: false,
        cargadoDesdeDB: false
      },
      logo: {
        archivo: null,
        preview: ''
      },
      establecimientos: [],
      nuevoEstablecimiento: { establecimiento: '', puntoEmision: '', descripcion: '' },
      availableAgenteTipos: [],
      rimpeTipos: [
        { value: 'rimpe', label: 'Contribuyente Régimen RIMPE' },
        { value: 'negocio_popular', label: 'Negocio Popular' }
      ]
    }
  },
  computed: {
    themeClass() {
      return themeManager.themeClass
    },
    establecimientosFiltrados() {
      return this.establecimientos
    },
    certCaducado() {
      if (!this.digitalCertificate.fechaCaducidad) return false
      return new Date(this.digitalCertificate.fechaCaducidad) < new Date()
    },
    certProximoAVencer() {
      if (!this.digitalCertificate.fechaCaducidad || this.certCaducado) return false
      const diffDias = (new Date(this.digitalCertificate.fechaCaducidad) - new Date()) / (1000 * 60 * 60 * 24)
      return diffDias <= 90
    },
    certExpiryClass() {
      if (this.certCaducado) return 'alert alert-danger'
      if (this.certProximoAVencer) return 'alert alert-warning'
      return 'alert alert-success'
    }
  },
  created() {
    this.cargarDatos()
  },
  methods: {
    async cargarDatos() {
      this.loading = true
      this.errorMessage = ''
      this.successMessage = ''
      try {
        const [empresaRes, facturacionRes, agentesRes, meRes] = await Promise.all([
          ConfiguracionService.getEmpresaConfig().catch(() => null),
          ConfiguracionService.getFacturacionConfig().catch(() => null),
          ConfiguracionService.getTiposAgenteRetencion().catch(() => null),
          fetchCurrentUser().catch(() => null)
        ])

        const empresaData = this.unwrapResponse(empresaRes)
        const facturacionData = this.unwrapResponse(facturacionRes)
        const agentesData = this.unwrapResponse(agentesRes)
        const meData = this.unwrapResponse(meRes)

        this.hidratarDesdeEmpresa(empresaData, meData)
        this.hidratarDesdeFacturacion(facturacionData)
        this.hidratarTiposAgente(agentesData)
      } catch (error) {
        this.errorMessage = this.normalizeError(error, 'No se pudo cargar la configuración de facturación.')
      } finally {
        this.loading = false
      }
    },
    unwrapResponse(response) {
      if (!response) return null
      if (response?.data !== undefined) return response.data
      return response
    },
    hidratarDesdeEmpresa(empresaData, meData) {
      // Respuesta: { success, data: { empresa: { id, ruc, razon_social, ... } } }
      const empresa = empresaData?.data?.empresa
        || empresaData?.empresa
        || empresaData?.data
        || empresaData
        || {}

      // ruc es DECIMAL en BD — convertir a string
      this.form.ruc = empresa.ruc ? String(empresa.ruc) : (meData?.user?.ruc ?? '')
      this.form.razonSocial   = empresa.razon_social    || meData?.user?.razon_social   || ''
      this.form.nombreComercial = empresa.nombre_comercial ?? this.form.nombreComercial
      this.form.ciudad          = empresa.ciudad           ?? this.form.ciudad
      this.form.telefonos       = empresa.telefono
        ? String(empresa.telefono)
        : (this.form.telefonos || '')
      this.form.direccion = empresa.direccion_matriz ?? this.form.direccion
    },
    hidratarDesdeFacturacion(config) {
      if (!config) return
      // Respuesta: { success, data: { configuracion_sri: {...}, establecimientos: [...], logo: {...}, firma: {...} } }
      const datos = config?.data || config
      const sri   = datos?.configuracion_sri || {}
      const establecimientos = datos?.establecimientos || []
      const logo  = datos?.logo || null
      const firma = datos?.firma || null

      if (sri.nombre_comercial) this.form.nombreComercial = sri.nombre_comercial
      // Preferir direccion_emisor; si no, direccion_matriz
      if (sri.direccion_emisor)  this.form.direccion = sri.direccion_emisor
      else if (sri.direccion_matriz) this.form.direccion = sri.direccion_matriz

      this.form.obligadoContabilidad = (sri.obligado_llevar_contabilidad === 'SI')

      // contribuyente_especial es BIGINT (0 = no aplica)
      if (sri.contribuyente_especial && Number(sri.contribuyente_especial) > 0) {
        this.form.contribuyenteEspecial.activo = true
        this.form.contribuyenteEspecial.codigo = String(sri.contribuyente_especial)
      }

      // condicion_tributaria: JSON con los regímenes especiales
      if (sri.condicion_tributaria) {
        const ct = typeof sri.condicion_tributaria === 'string'
          ? JSON.parse(sri.condicion_tributaria)
          : sri.condicion_tributaria
        this.form.regimenGeneral = !!ct.regimen_general
        if (ct.regimen_rimpe) {
          this.form.regimenRimpe.activo = !!ct.regimen_rimpe.activo
          this.form.regimenRimpe.tipo   = ct.regimen_rimpe.tipo || 'rimpe'
        }
        if (ct.gran_contribuyente) {
          this.form.granContribuyente.activo = !!ct.gran_contribuyente.activo
          this.form.granContribuyente.codigo = ct.gran_contribuyente.codigo || ''
        }
        if (ct.agente_retencion) {
          this.form.agenteRetencion.activo = !!ct.agente_retencion.activo
          this.form.agenteRetencion.tipo   = ct.agente_retencion.tipo || ''
        }
      }

      // Mensaje personalizado en facturas
      if (sri.mensaje_personalizado !== undefined && sri.mensaje_personalizado !== null) {
        this.form.mensajeFactura = sri.mensaje_personalizado
      }

      // Tipo de ambiente (1 = pruebas, 2 = producción)
      // El backend devuelve el campo como tipo_de_ambiente (nombre de columna en BD)
      const ambienteVal = sri.tipo_de_ambiente ?? sri.ambiente
      if (ambienteVal !== undefined && ambienteVal !== null) {
        this.form.ambiente = String(ambienteVal)
      }

      // --- Establecimientos: aplanar estructura anidada ---
      // Cada fila = un punto_emision con su establecimiento padre
      const codigoPrincipalEstab = sri.codigo_establecimiento
        ? String(sri.codigo_establecimiento).padStart(3, '0') : ''
      const codigoPrincipalPe = sri.codigo_punto_emision
        ? String(sri.codigo_punto_emision).padStart(3, '0') : ''

      if (Array.isArray(establecimientos) && establecimientos.length > 0) {
        const filas = []
        establecimientos.forEach(estab => {
          const codigoEstab = String(estab.codigo || '').padStart(3, '0')
          const puntosEmision = Array.isArray(estab.puntos_emision) ? estab.puntos_emision : []
          puntosEmision.forEach(pe => {
            const codigoPe = String(pe.codigo || '').padStart(3, '0')
            filas.push({
              establecimientoId: estab.id   || null,
              puntoEmisionId:    pe.id      || null,
              establecimiento:   codigoEstab,
              puntoEmision:      codigoPe,
              descripcion:       pe.descripcion || estab.nombre || '',
              secuencialFactura: pe.secuencial_factura != null ? Number(pe.secuencial_factura) : 0,
              predeterminado:    (codigoEstab === codigoPrincipalEstab && codigoPe === codigoPrincipalPe)
            })
          })
        })
        this.establecimientos = filas
        if (!filas.some(f => f.predeterminado) && filas.length > 0) {
          this.establecimientos[0].predeterminado = true
        }
        this.actualizarPuntoPrincipal()
      }

      // --- Logo (construir URL relativa al servidor) ---
      if (logo?.logo_ruta) {
        // VUE_APP_API_URL ejemplo: http://localhost/sicecomv1.0.1/backend_mvc/public/api
        // El logo se sirve desde la raíz del proyecto, no desde backend_mvc/public
        const base = (process.env.VUE_APP_API_URL || '')
          .replace(/\/backend_mvc\/public\/api\/?$/, '')
          .replace(/\/api\/?$/, '') // fallback si la URL tiene otro formato
        this.logo.preview = base ? base + '/' + logo.logo_ruta : '/' + logo.logo_ruta
      }

      // --- Firma electrónica activa ---
      if (firma) {
        if (firma.certificado_nombre) {
          this.digitalCertificate.nombreArchivo = firma.certificado_nombre
          this.digitalCertificate.cargadoDesdeDB = true
        }
        if (firma.fecha_vencimiento) {
          this.digitalCertificate.fechaCaducidad = firma.fecha_vencimiento
        }
      }
    },
    hidratarTiposAgente(data) {
      // data = { success, data: [...] } después de unwrapResponse
      const lista = Array.isArray(data)
        ? data
        : (Array.isArray(data?.data) ? data.data
          : (Array.isArray(data?.tipos) ? data.tipos : null))
      if (lista) {
        this.availableAgenteTipos = lista
      } else {
        this.availableAgenteTipos = [
          { value: 'general', label: 'Agente general (placeholder)' },
          { value: 'resolucion', label: 'Agente por resolución (placeholder)' }
        ]
      }
    },
    addEstablecimiento() {
      const establecimiento = this.nuevoEstablecimiento.establecimiento?.trim()
      const puntoEmision = this.nuevoEstablecimiento.puntoEmision?.trim()
      if (!establecimiento || !puntoEmision) {
        return
      }

      const nuevo = {
        establecimiento,
        puntoEmision,
        descripcion: this.nuevoEstablecimiento.descripcion?.trim() || '',
        secuencialFactura: 0,
        predeterminado: this.establecimientos.length === 0 || this.establecimientos.every(item => !item.predeterminado)
      }

      this.establecimientos.push(nuevo)
      this.nuevoEstablecimiento = { establecimiento: '', puntoEmision: '', descripcion: '' }
      this.actualizarPuntoPrincipal()
    },
    removeEstablecimiento(index) {
      this.establecimientos.splice(index, 1)
      if (!this.establecimientos.some(item => item.predeterminado) && this.establecimientos.length > 0) {
        this.establecimientos[0].predeterminado = true
      }
      this.actualizarPuntoPrincipal()
    },
    establecerPredeterminado(index) {
      this.establecimientos = this.establecimientos.map((item, idx) => ({
        ...item,
        predeterminado: idx === index
      }))
      this.actualizarPuntoPrincipal()
    },
    actualizarPuntoPrincipal() {
      const principal = this.establecimientos.find(item => item.predeterminado)
      if (principal && principal.establecimiento && principal.puntoEmision) {
        this.form.puntoEmisionPrincipal = `${principal.establecimiento}-${principal.puntoEmision}`
      } else {
        this.form.puntoEmisionPrincipal = ''
      }
    },
    onCertFileChange(event) {
      const file = event.target.files?.[0] || null
      this.digitalCertificate.archivo = file
      this.digitalCertificate.nombreArchivo = file ? file.name : ''
      this.digitalCertificate.cargadoDesdeDB = false
      if (!file) {
        this.digitalCertificate.fechaCaducidad = ''
      }
    },
    onLogoFileChange(event) {
      const file = event.target.files?.[0] || null
      this.logo.archivo = file
      if (!file) {
        this.logo.preview = ''
        return
      }
      const reader = new FileReader()
      reader.onload = e => {
        this.logo.preview = e.target.result
      }
      reader.readAsDataURL(file)
    },
    async validarCertificado() {
      if (!this.digitalCertificate.archivo || !this.digitalCertificate.clave) return
      this.validatingCert = true
      this.errorMessage = ''
      try {
        const formData = new FormData()
        formData.append('certificado', this.digitalCertificate.archivo)
        formData.append('clave', this.digitalCertificate.clave)
        const response = await ConfiguracionService.previewCertificadoDigital(formData)
        const data = this.unwrapResponse(response) || {}
        if (data.fecha_caducidad || data.fechaCaducidad) {
          this.digitalCertificate.fechaCaducidad = data.fecha_caducidad || data.fechaCaducidad
          this.successMessage = 'Certificado validado correctamente.'
        } else {
          this.successMessage = 'Certificado cargado. La fecha se actualizará cuando el backend procese el archivo.'
        }
      } catch (error) {
        this.errorMessage = this.normalizeError(error, 'No fue posible validar el certificado digital. Verifica la clave y el archivo.')
      } finally {
        this.validatingCert = false
      }
    },
    async solicitarAmbienteProduccion() {
      if (!confirm('¿Deseas solicitar el paso a ambiente de producción al SRI?')) return
      this.requestingAmbiente = true
      this.errorMessage = ''
      this.successMessage = ''
      try {
        await ConfiguracionService.requestAmbienteProduccion()
        this.successMessage = 'Solicitud enviada. Nuestro equipo se comunicará cuando el SRI habilite el ambiente de producción.'
      } catch (error) {
        this.errorMessage = this.normalizeError(error, 'No se pudo enviar la solicitud al SRI. Inténtalo nuevamente más tarde.')
      } finally {
        this.requestingAmbiente = false
      }
    },
    async guardarConfiguracion() {
      if (!this.validarAntesDeGuardar()) {
        return
      }
      this.saving = true
      this.errorMessage = ''
      this.successMessage = ''
      try {
        const payload = this.buildPayload()
        await ConfiguracionService.updateFacturacionConfig(payload)
        this.successMessage = 'Configuración de facturación guardada correctamente.'
      } catch (error) {
        this.errorMessage = this.normalizeError(error, 'No se pudo guardar la configuración de facturación.')
      } finally {
        this.saving = false
      }
    },
    validarAntesDeGuardar() {
      const issues = []
      const establecimientosValidos = this.establecimientos.filter(item => item.establecimiento && item.puntoEmision)
      if (establecimientosValidos.length === 0) {
        issues.push('Registra al menos un establecimiento con punto de emisión.')
      }
      if (this.form.contribuyenteEspecial.activo && !this.form.contribuyenteEspecial.codigo) {
        issues.push('Ingresa el código de contribuyente especial.')
      }
      if (this.form.granContribuyente.activo && !this.form.granContribuyente.codigo) {
        issues.push('Ingresa el código de gran contribuyente.')
      }
      if (this.form.regimenRimpe.activo && !this.form.regimenRimpe.tipo) {
        issues.push('Selecciona el tipo de contribuyente RIMPE.')
      }
      if (this.form.agenteRetencion.activo && !this.form.agenteRetencion.tipo) {
        issues.push('Selecciona el tipo de agente de retención.')
      }
      if (issues.length > 0) {
        this.errorMessage = issues.join(' ')
        return false
      }
      return true
    },
    buildPayload() {
      const establecimientos = this.establecimientos
        .filter(item => item.establecimiento && item.puntoEmision)
        .map(item => ({
          establecimiento: item.establecimiento,
          punto_emision: item.puntoEmision,
          descripcion: item.descripcion,
          secuencial_factura: item.secuencialFactura ?? 0,
          predeterminado: !!item.predeterminado
        }))

      const facturacion = {
        nombre_comercial: this.form.nombreComercial,
        ciudad: this.form.ciudad,
        telefonos: this.form.telefonos
          ? this.form.telefonos.split(',').map(item => item.trim()).filter(Boolean)
          : [],
        direccion: this.form.direccion,
        ambiente: this.form.ambiente,
        obligado_contabilidad: this.form.obligadoContabilidad,
        contribuyente_especial: this.form.contribuyenteEspecial,
        gran_contribuyente: this.form.granContribuyente,
        regimen_general: this.form.regimenGeneral,
        regimen_rimpe: this.form.regimenRimpe,
        agente_retencion: this.form.agenteRetencion,
        establecimientos,
        punto_emision_principal: this.form.puntoEmisionPrincipal,
        mensaje_factura: this.form.mensajeFactura,
        certificado: {
          fecha_caducidad: this.digitalCertificate.fechaCaducidad,
          firmar_automatico: this.digitalCertificate.firmarAutomaticamente
        }
      }

      const formData = new FormData()
      formData.append('facturacion', JSON.stringify(facturacion))
      if (this.digitalCertificate.archivo) {
        formData.append('certificado', this.digitalCertificate.archivo)
      }
      if (this.digitalCertificate.clave) {
        formData.append('clave_certificado', this.digitalCertificate.clave)
      }
      if (this.logo.archivo) {
        formData.append('logo', this.logo.archivo)
      }
      return formData
    },
    normalizeError(error, fallbackMessage) {
      if (error?.response?.data?.error) return error.response.data.error
      if (error?.message) return error.message
      return fallbackMessage
    }
  }
}
</script>

<style scoped>
.configuracion-facturacion {
  --primary-color: #1173d4;
  --primary-color-dark: #0f5fa3;
  --body-bg: #f5f7fb;
  --card-bg: #ffffff;
  --border-color: rgba(17, 115, 212, 0.1);
  --text-primary: #1f2933;
  --text-secondary: #4a5568;
  --table-header-bg: #f1f5fb;
  --table-border: #e2e8f0;
  --shadow: 0 14px 30px -12px rgba(15, 46, 70, 0.2);
  background-color: var(--body-bg);
  min-height: 100vh;
  padding-bottom: 2rem;
}

.configuracion-facturacion.dark-theme {
  --body-bg: #0b1320;
  --card-bg: #111d30;
  --border-color: rgba(148, 163, 184, 0.18);
  --text-primary: #e2e8f0;
  --text-secondary: #cbd5f5;
  --table-header-bg: rgba(17, 115, 212, 0.12);
  --table-border: rgba(148, 163, 184, 0.25);
  --shadow: none;
}

.card {
  background-color: var(--card-bg);
  border-color: var(--border-color);
  color: var(--text-primary);
  box-shadow: var(--shadow);
}

.bg-gradient {
  background: linear-gradient(135deg, rgba(17, 115, 212, 0.95) 0%, rgba(40, 137, 235, 0.9) 100%);
}

.configuracion-facturacion.dark-theme .bg-gradient {
  background: linear-gradient(135deg, rgba(29, 78, 216, 0.95) 0%, rgba(59, 130, 246, 0.85) 100%);
}

.section-card {
  border: 1px solid var(--border-color);
  border-radius: 18px;
  padding: 1.6rem;
  margin-bottom: 1.6rem;
  background-color: var(--card-bg);
  color: var(--text-primary);
  box-shadow: 0 10px 24px -18px rgba(15, 46, 70, 0.28);
}

.configuracion-facturacion.dark-theme .section-card {
  box-shadow: none;
}

.section-card__header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 1.2rem;
}

.section-card__header h5 {
  font-weight: 700;
  color: var(--text-primary);
  margin-bottom: 0.3rem;
}

.section-card__header small {
  color: var(--text-secondary);
}

.section-card__body label,
.section-card__body .form-label {
  font-weight: 600;
  color: var(--text-secondary);
}

.form-control,
.form-select,
.form-check-input,
textarea {
  background-color: var(--card-bg);
  color: var(--text-primary);
  border: 1px solid var(--table-border);
  border-radius: 10px;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
}

.form-control::placeholder,
textarea::placeholder {
  color: rgba(74, 85, 104, 0.6);
}

.form-control:focus,
.form-select:focus,
textarea:focus {
  border-color: var(--primary-color);
  box-shadow: 0 0 0 2px rgba(17, 115, 212, 0.15);
  background-color: var(--card-bg);
}

.configuracion-facturacion.dark-theme .form-control,
.configuracion-facturacion.dark-theme .form-select,
.configuracion-facturacion.dark-theme textarea {
  background-color: rgba(15, 23, 42, 0.6);
  border-color: rgba(148, 163, 184, 0.3);
  color: var(--text-primary);
}

.configuracion-facturacion.dark-theme .form-control::placeholder,
.configuracion-facturacion.dark-theme textarea::placeholder {
  color: rgba(226, 232, 240, 0.55);
}

.form-check-input {
  cursor: pointer;
}

.configuracion-facturacion.dark-theme .form-check-input {
  background-color: rgba(15, 23, 42, 0.6);
  border-color: rgba(148, 163, 184, 0.4);
}

.configuracion-facturacion.dark-theme .form-check-input:checked {
  background-color: var(--primary-color);
  border-color: var(--primary-color);
}

.table {
  color: var(--text-primary);
}

.table thead {
  background-color: var(--table-header-bg);
  color: var(--text-primary);
}

.table th,
.table td {
  border-color: var(--table-border);
}

.table tbody tr:hover {
  background-color: rgba(17, 115, 212, 0.04);
}

.configuracion-facturacion.dark-theme .table tbody tr:hover {
  background-color: rgba(59, 130, 246, 0.08);
}

.logo-preview {
  background: var(--card-bg);
  border-radius: 0.9rem;
  border: 1px dashed rgba(17, 115, 212, 0.45);
  text-align: center;
}

.logo-preview img {
  max-height: 120px;
  object-fit: contain;
  filter: drop-shadow(0 8px 12px rgba(15, 46, 70, 0.18));
}

.configuracion-facturacion.dark-theme .logo-preview {
  border-color: rgba(59, 130, 246, 0.35);
}

.btn-outline-primary,
.btn-outline-secondary,
.btn-primary {
  border-radius: 10px;
}

.btn-primary {
  background: linear-gradient(135deg, var(--primary-color) 0%, var(--primary-color-dark) 100%);
  border: none;
  box-shadow: 0 10px 18px -12px rgba(17, 115, 212, 0.65);
}

.btn-primary:hover {
  transform: translateY(-1px);
  box-shadow: 0 14px 22px -10px rgba(17, 115, 212, 0.7);
}

.configuracion-facturacion.dark-theme .btn-primary:hover {
  box-shadow: 0 14px 24px -12px rgba(59, 130, 246, 0.55);
}

.form-check-label,
.text-muted {
  color: var(--text-secondary) !important;
}

.alert {
  border-radius: 12px;
  padding: 1rem 1.2rem;
}

.spinner-border {
  width: 2rem;
  height: 2rem;
}

@media (max-width: 992px) {
  .section-card {
    padding: 1.25rem;
  }

  .section-card__header {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.4rem;
  }
}

@media (max-width: 576px) {
  .configuracion-facturacion {
    padding: 1rem 0;
  }

  .section-card {
    padding: 1rem;
    border-radius: 12px;
  }

  .card-header h3 {
    font-size: 1.15rem;
  }
}
</style>



